// User account page: change password + manage passkeys.

import { useEffect, useState } from 'react';
import { Fingerprint, Trash2, Plus, Check } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import { authApi, type PasskeySummary } from '../lib/api';
import type { User } from '@shared/types';

interface Props {
  user: User;
  passkeys: PasskeySummary[];
  onChange: () => void | Promise<void>;
}

export default function AccountPage({ user, passkeys, onChange }: Props) {
  const supportsPasskeys = typeof window !== 'undefined' && !!window.PublicKeyCredential;
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const [welcomed, setWelcomed] = useState(params.get('welcome') === '1');

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-2xl mx-auto py-6 px-4 sm:py-10 sm:px-6 space-y-8">
        <header>
          <h1 className="font-display text-2xl text-slate-900">Account</h1>
          <p className="text-sm text-slate-500 mt-1">{user.email}</p>
        </header>

        {welcomed && (
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3 flex items-center gap-2 text-sm text-blue-900">
            <Check size={14} className="text-blue-700" />
            You're all set. Consider adding a passkey for faster future sign-ins.
            <button onClick={() => setWelcomed(false)} className="ml-auto text-xs text-blue-700">Dismiss</button>
          </div>
        )}

        <section className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 space-y-4">
          <h2 className="font-display text-lg text-slate-900">Passkeys</h2>
          <p className="text-sm text-slate-500">Sign in without a password. Each device you trust gets its own passkey.</p>
          {!supportsPasskeys && <p className="text-xs text-amber-700">This browser doesn't support passkeys.</p>}
          <PasskeyList passkeys={passkeys} onChange={onChange} />
          {supportsPasskeys && <AddPasskey onAdded={onChange} />}
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 space-y-4">
          <h2 className="font-display text-lg text-slate-900">Change password</h2>
          <ChangePasswordForm />
        </section>
      </div>
    </div>
  );
}

function PasskeyList({ passkeys, onChange }: { passkeys: PasskeySummary[]; onChange: () => void | Promise<void> }) {
  if (passkeys.length === 0) {
    return <p className="text-xs text-slate-500 italic">No passkeys yet.</p>;
  }
  async function remove(id: string, label: string | null) {
    if (!confirm(`Remove "${label || id.slice(0, 8) + '…'}"?`)) return;
    await authApi.passkeyDelete(id);
    await onChange();
  }
  return (
    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
      {passkeys.map((pk) => (
        <li key={pk.id} className="px-3 py-2.5 flex items-center gap-3">
          <Fingerprint size={14} className="text-slate-400" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-800 truncate">{pk.device_label || `Passkey ${pk.id.slice(0, 6)}…`}</div>
            <div className="text-xs text-slate-500">
              Added {new Date(pk.created_at).toLocaleDateString()}
              {pk.last_used_at ? ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}` : ''}
            </div>
          </div>
          <button onClick={() => remove(pk.id, pk.device_label)} className="btn-ghost text-slate-400 hover:text-red-600">
            <Trash2 size={13} />
          </button>
        </li>
      ))}
    </ul>
  );
}

function AddPasskey({ onAdded }: { onAdded: () => void | Promise<void> }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    setErr(null);
    setBusy(true);
    try {
      const opts = await authApi.passkeyRegisterStart();
      const response = await startRegistration(opts as Parameters<typeof startRegistration>[0]);
      await authApi.passkeyRegisterFinish(response, label.trim() || null);
      setLabel('');
      await onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not enroll passkey');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-dashed border-slate-200 rounded-md p-3 flex flex-col gap-2">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Device label (e.g. iPhone, MacBook)"
        className="input"
      />
      <button onClick={add} disabled={busy} className="btn-secondary justify-center py-2">
        <Plus size={13} />
        {busy ? 'Adding…' : 'Add a passkey'}
      </button>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Clear stale messages when inputs change
  useEffect(() => {
    if (msg || err) {
      const id = setTimeout(() => { setMsg(null); setErr(null); }, 5000);
      return () => clearTimeout(id);
    }
  }, [msg, err]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    if (next.length < 8) return setErr('New password must be at least 8 characters');
    if (next !== confirm) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await authApi.passwordChange(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      setMsg('Password updated.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <input type="password" autoComplete="current-password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className="input" required />
      <input type="password" autoComplete="new-password" placeholder="New password (8+ chars)" value={next} onChange={(e) => setNext(e.target.value)} className="input" required minLength={8} />
      <input type="password" autoComplete="new-password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input" required minLength={8} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      {msg && <p className="text-xs text-emerald-600">{msg}</p>}
      <button type="submit" disabled={busy} className="btn-primary justify-center py-2">{busy ? 'Saving…' : 'Update password'}</button>
    </form>
  );
}
